import { useCallback, useEffect, useState } from "react";
import { MemberSearch } from "@/components/tpr/MemberSearch/MemberSearch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnOrganisationOwner } from "@/core/auth";
import { getInitials } from "@/lib/utils";
import { MemberService } from "@/services/MemberService";
import { OrganisationService } from "@/services/OrganisationService";
import { X } from "lucide-react";
import { useMakeABookingDialog } from "../useMakeABookingDialog";
import { resolveOwnedOrgId, rosterToMessageableMembers } from "./orgMemberFetch";
import type { OrganisationMember } from "@/api/organisationApi";
import type { MessageableEntity } from "@/types/MessageableEntity";

export const MemberSelector = () => {
  const { formData, updateFormData, isElevatedUser, currentUser, isSubmitting } = useMakeABookingDialog();
  const memberService = new MemberService();
  const fetchMembers = useCallback((searchTerm: string) => memberService.searchActiveMembers(searchTerm), []);

  const { member: fullCurrentUser } = useMember();
  const isOrgOwner = !isElevatedUser && isMemberAnOrganisationOwner(fullCurrentUser);
  const ownedOrgId = resolveOwnedOrgId(fullCurrentUser);

  const [orgRoster, setOrgRoster] = useState<OrganisationMember[]>([]);
  const [rosterError, setRosterError] = useState(false);

  useEffect(() => {
    if (!isOrgOwner || !ownedOrgId) return;
    let cancelled = false;
    const svc = new OrganisationService();
    svc
      .checkIsOwnerAndGetMembers(ownedOrgId)
      .then((res) => {
        if (cancelled) return;
        if (res?.isOwner) {
          setOrgRoster(res.members);
          setRosterError(false);
        } else {
          setRosterError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setRosterError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOrgOwner, ownedOrgId]);

  const fetchOrgMembers = useCallback(
    (searchTerm: string) => Promise.resolve(rosterToMessageableMembers(orgRoster, searchTerm)),
    [orgRoster],
  );

  const handleMemberSelect = (entity: MessageableEntity) => {
    if (entity.type === "member") {
      updateFormData({
        memberId: entity.id,
        memberName: `${entity.firstName} ${entity.lastName}`,
      });
    }
  };

  const handleClearSelection = () => {
    // Reset to current user
    if (currentUser) {
      updateFormData({
        memberId: currentUser.id,
        memberName: `${currentUser.firstName} ${currentUser.lastName}`,
      });
    }
  };

  const isBookingForSelf = formData.memberId === currentUser?.id;

  // Organisation owners (not elevated) may book for a member of their own org. Reuse the elevated
  // search UI, but the fetch is restricted to the owner's own roster — a non-org member can never
  // appear. If the roster can't be loaded, fall through to the read-only "booking for you" block.
  if (!formData.memberLocked && isOrgOwner && !rosterError) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
          <Avatar className="h-8 w-8">
            <AvatarImage src={undefined} />
            <AvatarFallback>{getInitials(formData.memberName)}</AvatarFallback>
          </Avatar>
          <span className="font-medium">{formData.memberName}</span>
          {isBookingForSelf && <span className="text-xs text-gray-500">(You)</span>}
          {!isBookingForSelf && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-6 w-6 p-0"
              onClick={handleClearSelection}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p data-testid="org-owner-member-search-hint" className="text-sm text-gray-500">
          Please click here and search for the member in your organisation you wish to assign this booking to.
        </p>
        <MemberSearch
          onSelect={handleMemberSelect}
          selectedEntities={[]}
          excludeGroups={true}
          placeholder="Search your organisation…"
          fetchEntities={fetchOrgMembers}
        />
      </div>
    );
  }

  // If member is locked or user is not elevated, just show who the booking is for
  if (formData.memberLocked || !isElevatedUser) {
    return (
      <div className="space-y-2">
        {/* <Label className="text-sm font-medium">Booking for</Label> */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
          <Avatar className="h-8 w-8">
            <AvatarImage src={currentUser?.avatar || undefined} />
            <AvatarFallback>{getInitials(formData.memberName)}</AvatarFallback>
          </Avatar>
          <span className="font-medium">{formData.memberName}</span>
          {formData.memberLocked && <span className="text-xs text-gray-500 ml-auto">(Locked)</span>}
        </div>
      </div>
    );
  }

  // Elevated user can select another member
  return (
    <div className="space-y-2">
      {/* <Label className="text-sm font-medium">Booking for</Label> */}

      {/* Current selection */}
      <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
        <Avatar className="h-8 w-8">
          <AvatarImage src={undefined} />
          <AvatarFallback>{getInitials(formData.memberName)}</AvatarFallback>
        </Avatar>
        <span className="font-medium">{formData.memberName}</span>
        {isBookingForSelf && <span className="text-xs text-gray-500">(You)</span>}
        {!isBookingForSelf && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-6 w-6 p-0"
            onClick={handleClearSelection}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search for another member */}
      <MemberSearch
        onSelect={handleMemberSelect}
        selectedEntities={[]}
        excludeGroups={true}
        placeholder="Search for another member..."
        fetchEntities={fetchMembers}
      />
    </div>
  );
};

export default MemberSelector;
